/**
 * @fileoverview Modal dialog for entering a Lead # to jump to inside the PDF.
 * @module ui/PdfLeadGoToDialog
 */

import { BaseDialog } from './BaseDialog.js';


/**
 * Modal dialog with a single input for typing a Lead # / bookmark label.
 * Resolves to { action: 'go', leadInput } or { action: 'cancel' }.
 * @class
 * @extends BaseDialog
 */
export class PdfLeadGoToDialog extends BaseDialog {

    constructor(options = {}) {
        super({ idPrefix: 'pdfLeadGoToDialog' });
        this.lastInput = options.lastInput || '';
    }


    /**
     * @returns {string}
     * @override
     */
    getModalHtml() {
        const bodyHtml = `
            <div class="mb-2">
                <label for="${this.modalId}_leadInput" class="form-label">
                    Jump to PDF bookmark by Lead # (e.g. <code>1-1234</code>) or page # (e.g. <code>42</code> or <code>P42</code>):
                </label>
                <input
                    type="text"
                    class="form-control"
                    id="${this.modalId}_leadInput"
                    placeholder="Lead # or page #"
                    autocomplete="off"
                    spellcheck="false"
                >
            </div>
        `;

        const footerHtml = `
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" id="${this.modalId}_goBtn">
                <i class="bi bi-arrow-right-circle"></i> Go
            </button>
        `;

        return this.buildModalHtml({
            title: 'Go to Lead',
            icon: 'bi-bookmark',
            bodyHtml,
            footerHtml,
            size: 'modal-md'
        });
    }


    /** @override */
    onBeforeShow() {
        const input = this.getElement('_leadInput');
        if (input && this.lastInput) {
            input.value = this.lastInput;
        }
    }


    /** @override */
    onAfterShow() {
        const input = this.getElement('_leadInput');
        if (input) {
            input.focus();
            input.select();
        }
    }


    /** @override */
    setupEventListeners() {
        const input = this.getElement('_leadInput');
        const goBtn = this.getElement('_goBtn');

        const submit = () => {
            const leadInput = input.value.trim();
            if (leadInput) {
                this.resolve({ action: 'go', leadInput });
            }
        };

        goBtn.addEventListener('click', submit);

        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                submit();
            }
        });
    }


    /** @override */
    getDefaultResult() {
        return { action: 'cancel', leadInput: null };
    }


    /**
     * Shows the dialog (singleton).
     * @param {Object} [options={}]
     * @returns {Promise<{action: string, leadInput: string|null}>}
     */
    static async showPdfLeadGoToDialog(options = {}) {
        return BaseDialog.showSingleton(PdfLeadGoToDialog, options);
    }
}
